import type { DrawingTool } from "../canvas/canvasTypes";
import type { ShapeTool } from "../shapes/shapeTypes";
import { DOCUMENT_SCHEMA_VERSION, type PersistedLayerState } from "../layers/layerTypes";

export const COMIC_DOCUMENT_SCHEMA_VERSION = DOCUMENT_SCHEMA_VERSION;
export { DOCUMENT_SCHEMA_VERSION } from "../layers/layerTypes";
export const MAX_COMIC_OBJECTS = 200;
export const MAX_BUBBLE_TEXT_LENGTH = 500;

export type ComicTemplateId =
  "one-panel" | "two-panel" | "three-panel" | "four-panel" | "six-panel";

export const COMIC_TEMPLATE_IDS: readonly ComicTemplateId[] = [
  "one-panel",
  "two-panel",
  "three-panel",
  "four-panel",
  "six-panel",
];

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ComicPanel extends Rect {
  id: string;
  templateSlot: number;
  order: number;
}

export type ComicBubbleType = "speechBubble" | "thoughtBubble" | "shoutBubble" | "narrationBox";

export type BubbleTextAlign = "left" | "center";
export type BubbleTailDirection = "bottom-left" | "bottom-right" | "left" | "right";

export interface ComicBubbleData {
  text: string;
  fontSize: number;
  textAlign: BubbleTextAlign;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  tailDirection?: BubbleTailDirection;
}

export interface ComicBubbleObject extends Rect {
  id: string;
  type: ComicBubbleType;
  panelId: string;
  rotation: number;
  layerId: string;
  order: number;
  data: ComicBubbleData;
}

export interface PersistedDocumentState {
  schemaVersion: typeof COMIC_DOCUMENT_SCHEMA_VERSION;
  activeLayerId: string;
  layers: PersistedLayerState[];
  comicTemplateId?: ComicTemplateId;
  panels: ComicPanel[];
  objects: ComicBubbleObject[];
}

export type ComicAddTool =
  "addSpeechBubble" | "addThoughtBubble" | "addShoutBubble" | "addNarrationBox";

export type RasterSelectionTool = "selectRasterArt";
export type RasterUtilityTool = "fill" | "pickColour";
export type StudioTool =
  | DrawingTool
  | RasterSelectionTool
  | RasterUtilityTool
  | ShapeTool
  | "selectComicObject"
  | ComicAddTool;

export interface ComicSelectionState {
  panelId: string | null;
  object: ComicBubbleObject | null;
}

export function isComicTemplateId(value: unknown): value is ComicTemplateId {
  return typeof value === "string" && COMIC_TEMPLATE_IDS.includes(value as ComicTemplateId);
}

export function isComicBubbleType(value: unknown): value is ComicBubbleType {
  return (
    value === "speechBubble" ||
    value === "thoughtBubble" ||
    value === "shoutBubble" ||
    value === "narrationBox"
  );
}

export function bubbleTypeForTool(tool: ComicAddTool): ComicBubbleType {
  switch (tool) {
    case "addThoughtBubble":
      return "thoughtBubble";
    case "addShoutBubble":
      return "shoutBubble";
    case "addNarrationBox":
      return "narrationBox";
    default:
      return "speechBubble";
  }
}

export function isDrawingStudioTool(tool: StudioTool): tool is DrawingTool {
  return tool === "pencil" || tool === "marker" || tool === "crayon" || tool === "eraser";
}

export function isRasterStudioTool(
  tool: StudioTool,
): tool is DrawingTool | RasterSelectionTool | RasterUtilityTool | ShapeTool {
  return (
    isDrawingStudioTool(tool) ||
    tool === "selectRasterArt" ||
    tool === "fill" ||
    tool === "pickColour" ||
    tool === "line" ||
    tool === "rectangle" ||
    tool === "ellipse"
  );
}
